import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Wallet, Loader2, Plus, Ban, Tags, FileDown, AlertTriangle, Upload,
  Pencil, Check, X,
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { API } from "../../App";
import { InternalStockUseDialog } from "./finance/InternalStockUseDialog";
import { ExternalCarWashDialog } from "./finance/ExternalCarWashDialog";
import { RedoWarrantyCostDialog } from "./finance/RedoWarrantyCostDialog";
import { StockValuationCard } from "./StockValuationCard";
import PeriodFilter from "./finance/PeriodFilter";
import PnlChart from "./finance/PnlChart";
import RecurringTab from "./finance/RecurringTab";
import { VatPreview, VatRateSelect } from "./finance/VatRateSelect";
import { money, presetRate, rateError } from "./finance/constants";

const TABS = ["overview", "expenses", "recurring", "categories"];

const Row = ({ label, value, pct, bold, negative }) => (
  <div className={`flex justify-between gap-4 py-2 border-b border-slate-800 ${bold ? "text-white font-semibold" : "text-slate-300"}`}>
    <span className="text-sm">{label}</span>
    <span className="text-sm font-mono whitespace-nowrap">
      {negative && Number(value || 0) !== 0 ? `(${money(value)})` : money(value)}
      {pct != null && <span className="text-slate-500 ml-2">{pct.toFixed(1)}%</span>}
    </span>
  </div>
);

const Kpi = ({ label, value, tone, testid }) => (
  <div className="rounded border border-slate-700 bg-slate-800/40 p-3" data-testid={testid}>
    <p className="text-[10px] uppercase tracking-wide text-slate-500">{label}</p>
    <p className={`text-base font-mono ${tone || "text-white"}`}>{money(value)}</p>
  </div>
);

const FinanceSection = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState("overview");
  const [range, setRange] = useState({
    period: searchParams.get("fperiod") || "month",
    from: searchParams.get("ffrom") || null,
    to: searchParams.get("fto") || null,
  });
  const [bounds, setBounds] = useState(null);
  const [pnl, setPnl] = useState(null);
  const [series, setSeries] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [form, setForm] = useState({
    expense_date: new Date().toISOString().slice(0, 10),
    category_id: "", payee: "", description: "", amount_gross: "",
    vat_treatment: "standard_23", vat_rate_percent: 23, payment_method: "",
  });
  const [newCategory, setNewCategory] = useState({
    name: "", group: "other", default_vat_treatment: "standard_23",
    default_vat_rate_percent: 23,
  });
  const [editingCategory, setEditingCategory] = useState(null);
  const [catForm, setCatForm] = useState({});
  const [archivedPrompt, setArchivedPrompt] = useState(null);
  const [showInternalUse, setShowInternalUse] = useState(false);
  const [showCarWash, setShowCarWash] = useState(false);
  const [showRedoCost, setShowRedoCost] = useState(false);
  const [templateWarning, setTemplateWarning] = useState(null);

  const query = useMemo(() => {
    const p = new URLSearchParams({ period: range.period });
    if (range.from) p.set("from", range.from);
    if (range.to) p.set("to", range.to);
    return p.toString();
  }, [range]);

  // The selection lives in the URL, so a refresh or a shared link keeps it.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    next.set("fperiod", range.period);
    if (range.from) next.set("ffrom", range.from); else next.delete("ffrom");
    if (range.to) next.set("fto", range.to); else next.delete("fto");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range]);

  useEffect(() => {
    axios.get(`${API}/accounting/date-bounds`)
      .then((r) => setBounds(r.data))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const p = await axios.get(`${API}/accounting/pnl?${query}`);
      const start = p.data.period.start;
      const end = p.data.period.end_inclusive;
      const [s, e, c] = await Promise.all([
        axios.get(`${API}/accounting/series?${query}`),
        axios.get(`${API}/accounting/expenses?status=all&limit=1000&from=${start}&to=${end}`),
        axios.get(`${API}/accounting/categories?include_inactive=true`),
      ]);
      setPnl(p.data);
      setSeries(s.data);
      setExpenses(e.data.expenses || []);
      setCategories(c.data.categories || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load finance data");
    } finally {
      setIsLoading(false);
    }
  }, [query]);

  useEffect(() => { load(); }, [load]);

  const addExpense = async () => {
    if (!form.category_id || !form.amount_gross) {
      toast.error("Category and amount are required");
      return;
    }
    const err = rateError(form.vat_treatment, form.vat_rate_percent);
    if (err) {
      toast.error(err);
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/accounting/expenses`, {
        ...form, amount_gross: Number(form.amount_gross),
        vat_rate_percent: form.vat_rate_percent === "" ? null : Number(form.vat_rate_percent),
      });
      toast.success(
        r.data.possible_duplicate
          ? "Saved — but an identical expense already exists for that date"
          : "Expense saved"
      );
      setForm({ ...form, payee: "", description: "", amount_gross: "" });
      load();
    } catch (err2) {
      toast.error(err2.response?.data?.detail || "Could not save expense");
    } finally { setBusy(false); }
  };

  const voidExpense = async (id) => {
    setBusy(true);
    try {
      await axios.post(`${API}/accounting/expenses/${id}/void`);
      toast.success("Expense voided");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not void");
    } finally { setBusy(false); }
  };

  const uploadReceipt = async (id, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await axios.post(`${API}/accounting/expenses/${id}/receipt`, fd);
      toast.success("Receipt attached");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    }
  };

  const startEdit = (e) => {
    setEditing(e.expense_id);
    setEditForm({
      expense_date: e.expense_date,
      category_id: e.category_id || "",
      payee: e.payee || "",
      description: e.description || "",
      amount_gross: e.amount_gross == null ? "" : String(e.amount_gross),
      vat_treatment: e.vat_treatment || "standard_23",
      vat_rate_percent: e.vat_rate_percent ?? presetRate(e.vat_treatment) ?? 0,
    });
  };

  const saveEdit = async () => {
    const err = rateError(editForm.vat_treatment, editForm.vat_rate_percent);
    if (err) {
      toast.error(err);
      return;
    }
    setBusy(true);
    try {
      const r = await axios.patch(`${API}/accounting/expenses/${editing}`, {
        ...editForm,
        amount_gross: editForm.amount_gross === "" ? null : Number(editForm.amount_gross),
        vat_rate_percent: editForm.vat_rate_percent === ""
          ? null : Number(editForm.vat_rate_percent),
      });
      toast.success(r.data.status === "active" ? "Expense updated" : "Saved as draft");
      setEditing(null);
      load();
    } catch (err2) {
      toast.error(err2.response?.data?.detail || "Could not update expense");
    } finally { setBusy(false); }
  };

  const createCategory = async (force = false) => {
    const name = newCategory.name.trim();
    if (!name) return;
    const err = rateError(newCategory.default_vat_treatment,
                          newCategory.default_vat_rate_percent);
    if (err) {
      toast.error(err);
      return;
    }
    const archived = categories.find(
      (c) => !c.active && c.name.trim().toLowerCase() === name.toLowerCase());
    if (archived && !force) {
      setArchivedPrompt(archived);
      return;
    }
    try {
      await axios.post(`${API}/accounting/categories`, {
        ...newCategory, name,
        default_vat_rate_percent: newCategory.default_vat_rate_percent === ""
          ? null : Number(newCategory.default_vat_rate_percent),
      });
      toast.success("Category added");
      setNewCategory({ name: "", group: "other",
                       default_vat_treatment: "standard_23", default_vat_rate_percent: 23 });
      setArchivedPrompt(null);
      load();
    } catch (err2) {
      toast.error(err2.response?.data?.detail || "Could not add category");
    }
  };

  const patchCategory = async (categoryId, body, params = "") => {
    try {
      const r = await axios.patch(
        `${API}/accounting/categories/${categoryId}${params}`, body);
      if (r.data.templates_deactivated) {
        toast.success(`Saved · ${r.data.templates_deactivated} recurring template(s) stopped`);
      } else {
        toast.success("Saved");
      }
      setTemplateWarning(null);
      setEditingCategory(null);
      load();
      return true;
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.code === "active_templates_attached") {
        setTemplateWarning({ categoryId, body, ...detail });
        return false;
      }
      toast.error(typeof detail === "string" ? detail : "Could not update");
      return false;
    }
  };

  const toggleCategory = (cat) =>
    patchCategory(cat.category_id, { active: !cat.active });

  const saveCategoryEdit = () => {
    const err = rateError(catForm.default_vat_treatment, catForm.default_vat_rate_percent);
    if (err) {
      toast.error(err);
      return;
    }
    return patchCategory(editingCategory, {
      ...catForm,
      default_vat_rate_percent: catForm.default_vat_rate_percent === ""
        ? null : Number(catForm.default_vat_rate_percent),
    });
  };

  const download = async (what, filename) => {
    try {
      const r = await axios.get(`${API}/accounting/export/${what}?${query}`,
                                { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Export failed");
    }
  };

  const activeCategories = useMemo(() => categories.filter((c) => c.active), [categories]);
  const slug = pnl ? `${pnl.period.start}-to-${pnl.period.end_inclusive}` : "period";

  return (
    <>
    <Card className="bg-slate-900 border-slate-700" data-testid="finance-section">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle className="text-lg font-rajdhani text-white flex items-center gap-2">
            <Wallet className="w-5 h-5 text-saturn-orange" /> Finance
          </CardTitle>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" className="border-slate-600 text-slate-200 h-8"
                    onClick={() => download("pnl.pdf", `profit-and-loss-${slug}.pdf`)}
                    data-testid="finance-export-pdf">
              <FileDown className="w-3 h-3 mr-1" /> P&amp;L PDF
            </Button>
            <Button size="sm" variant="outline" className="border-slate-600 text-slate-200 h-8"
                    onClick={() => download("expenses.csv", `expense-ledger-${slug}.csv`)}
                    data-testid="finance-export-csv">
              <FileDown className="w-3 h-3 mr-1" /> Ledger CSV
            </Button>
          </div>
        </div>
        <div className="mt-3">
          <PeriodFilter value={range} bounds={bounds} onChange={setRange}
                        rangeLabel={pnl?.period?.range_label} />
        </div>
        <div className="flex flex-wrap gap-1 mt-3">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)}
                    data-testid={`finance-tab-${t}`}
                    className={`text-xs px-3 py-1.5 rounded-full capitalize transition-colors ${
                      tab === t ? "bg-saturn-orange text-black font-semibold"
                                : "text-slate-400 hover:text-white hover:bg-slate-800"}`}>
              {t}
            </button>
          ))}
        </div>
      </CardHeader>

      <CardContent>
        {isLoading && (
          <div className="flex justify-center py-10">
            <Loader2 className="w-6 h-6 animate-spin text-saturn-orange" />
          </div>
        )}

        {!isLoading && tab === "overview" && pnl && (
          <div className="space-y-4" data-testid="finance-overview">
            {pnl.warnings?.map((w) => (
              <p key={w} className="text-xs flex gap-2 items-start rounded border border-amber-500/40 bg-amber-500/5 text-amber-200 px-3 py-2"
                 data-testid="finance-warning">
                <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" /> {w}
              </p>
            ))}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2" data-testid="finance-kpis">
              <Kpi label="Revenue (net)" value={pnl.revenue_net} tone="text-emerald-400"
                   testid="kpi-revenue" />
              <Kpi label="Cost of sales" value={pnl.cost_of_sales.total} tone="text-orange-400"
                   testid="kpi-cost-of-sales" />
              <Kpi label="Overheads" value={pnl.operating_expenses.total} tone="text-slate-300"
                   testid="kpi-overheads" />
              <Kpi label="Net profit" value={pnl.net_operating_profit}
                   tone={pnl.net_operating_profit >= 0 ? "text-sky-400" : "text-red-400"}
                   testid="kpi-net-profit" />
            </div>

            {series && (
              <PnlChart buckets={series.buckets} granularity={series.granularity}
                        onMonthClick={(m) => setRange({ period: "month", from: m, to: null })} />
            )}

            <p className="text-xs text-slate-500">{pnl.period.label} · {pnl.invoice_count} invoice(s)</p>
            <div>
              <Row label="Revenue (net of VAT)" value={pnl.revenue_net} bold />
              <Row label="Materials used on jobs" value={pnl.cost_of_sales.materials} negative />
              {/* Its own cost-of-sales line, CARVED OUT of Materials — the
                  totals below do not move, the figure just stops hiding
                  inside Materials. Click for the month-end breakdown. */}
              <button
                type="button"
                onClick={() => setShowCarWash(true)}
                className="w-full flex items-baseline justify-between gap-2 text-left hover:bg-slate-800/40 rounded px-1 transition-colors"
                data-testid="finance-external-car-wash-row"
              >
                <span className="text-sm text-slate-300">
                  External car wash
                  <span className="text-[11px] text-slate-500 ml-2">
                    {pnl.external_car_wash?.wash_count || 0} wash(es) · click for detail
                  </span>
                </span>
                <span className="text-sm font-mono text-orange-300"
                      data-testid="finance-external-car-wash-total">
                  ({money(pnl.cost_of_sales.external_car_wash)})
                </span>
              </button>
              {/* Redo & warranty cost — free return visits. Unlike the wash
                  above this is ADDED, not carved out: a redo has no invoice,
                  so it is new cost of sales in the month it was completed. */}
              <button
                type="button"
                onClick={() => setShowRedoCost(true)}
                className="w-full flex items-baseline justify-between gap-2 text-left hover:bg-slate-800/40 rounded px-1 transition-colors"
                data-testid="finance-redo-warranty-row"
              >
                <span className="text-sm text-slate-300">
                  Redo &amp; warranty cost
                  <span className="text-[11px] text-slate-500 ml-2">
                    {pnl.redo_warranty?.redo_count || 0} return visit(s) · click for detail
                  </span>
                </span>
                <span className="text-sm font-mono text-orange-300"
                      data-testid="finance-redo-warranty-total">
                  ({money(pnl.cost_of_sales.redo_warranty)})
                </span>
              </button>
              <Row label="Labour on jobs" value={pnl.cost_of_sales.labour} negative />
              <Row label="Stock write-offs / losses" value={pnl.cost_of_sales.stock_write_offs} negative />
              <Row label="Gross profit" value={pnl.gross_profit} pct={pnl.gross_profit_pct} bold />
              {Object.entries(pnl.operating_expenses.by_category).map(([name, c]) => (
                <Row key={name} label={`  ${name}`} value={c.amount} negative />
              ))}
              <Row label="Operating expenses" value={pnl.operating_expenses.total} negative bold />
              <Row label="Net operating profit" value={pnl.net_operating_profit}
                   pct={pnl.net_operating_profit_pct} bold />
              {/* BELOW the profit line, and deliberately NOT subtracted
                  from it — the accountant decides the treatment, so it is
                  never booked as an overhead. */}
              {pnl.below_the_line && (
                <div className="pt-2 mt-1 border-t border-slate-700"
                     data-testid="finance-below-the-line">
                  <button
                    type="button"
                    onClick={() => setShowInternalUse(true)}
                    className="w-full flex items-baseline justify-between gap-2 text-left hover:bg-slate-800/40 rounded px-1 py-1 transition-colors"
                    data-testid="finance-internal-use-row"
                  >
                    <span className="text-sm text-slate-300">
                      Internal use of stock
                      <span className="text-[11px] text-slate-500 ml-2">
                        not a customer-job cost
                      </span>
                    </span>
                    <span className="text-sm font-mono text-sky-300"
                          data-testid="finance-internal-use-total">
                      {money(pnl.below_the_line.internal_stock_use)}
                    </span>
                  </button>
                  <p className="text-[11px] text-slate-500 px-1">
                    {pnl.below_the_line.internal_stock_use_lines} line(s) across{" "}
                    {pnl.below_the_line.internal_stock_use_jobs} job(s). Excluded
                    from cost of sales and from every job&apos;s margin; not
                    booked as an overhead.
                    {pnl.below_the_line.internal_stock_use_uncosted_lines > 0 && (
                      <span className="text-amber-400">
                        {" "}{pnl.below_the_line.internal_stock_use_uncosted_lines} line(s)
                        have no unit cost recorded and are valued at €0.
                      </span>
                    )}
                  </p>
                  {pnl.below_the_line.goodwill_in_job_cost > 0 && (
                    <p className="text-[11px] text-amber-300/80 px-1 mt-0.5"
                       data-testid="finance-goodwill-total">
                      Goodwill to customers {money(pnl.below_the_line.goodwill_in_job_cost)}
                      {" "}— already inside cost of sales above.
                    </p>
                  )}
                </div>
              )}
            </div>
            {pnl.comparisons && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="finance-comparisons">
                {Object.values(pnl.comparisons).map((c) => (
                  <div key={c.label} className="rounded border border-slate-700 bg-slate-800/40 p-3">
                    <p className="text-[10px] uppercase tracking-wide text-slate-500">{c.label}</p>
                    <p className="text-sm text-white font-mono">{money(c.net_operating_profit)}</p>
                    <p className="text-[11px] text-slate-500">rev {money(c.revenue_net)}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {!isLoading && tab === "expenses" && (
          <div className="space-y-4" data-testid="finance-expenses">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div>
                <Label className="text-xs text-slate-400">Date</Label>
                <Input type="date" value={form.expense_date} min={bounds?.min_date}
                       max={bounds?.max_date}
                       onChange={(e) => setForm({ ...form, expense_date: e.target.value })}
                       className="bg-slate-800 border-slate-700 text-white h-9"
                       data-testid="expense-date-input" />
              </div>
              <div>
                <Label className="text-xs text-slate-400">Category</Label>
                <Select value={form.category_id}
                        onValueChange={(v) => {
                          const cat = categories.find((c) => c.category_id === v);
                          setForm({ ...form, category_id: v,
                                    vat_treatment: cat?.default_vat_treatment || form.vat_treatment,
                                    vat_rate_percent: cat?.default_vat_rate_percent
                                      ?? form.vat_rate_percent });
                        }}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white h-9"
                                 data-testid="expense-category-select">
                    <SelectValue placeholder="Pick a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {activeCategories.map((c) => (
                      <SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-400">Amount (incl. VAT)</Label>
                <Input type="number" step="0.01" value={form.amount_gross}
                       onChange={(e) => setForm({ ...form, amount_gross: e.target.value })}
                       className="bg-slate-800 border-slate-700 text-white h-9"
                       data-testid="expense-amount-input" />
                <VatPreview gross={form.amount_gross} treatment={form.vat_treatment}
                            ratePercent={form.vat_rate_percent} prefix="expense" />
              </div>
              <div>
                <Label className="text-xs text-slate-400">Payee</Label>
                <Input value={form.payee} onChange={(e) => setForm({ ...form, payee: e.target.value })}
                       className="bg-slate-800 border-slate-700 text-white h-9"
                       data-testid="expense-payee-input" />
              </div>
              <div>
                <Label className="text-xs text-slate-400">Description</Label>
                <Input value={form.description}
                       onChange={(e) => setForm({ ...form, description: e.target.value })}
                       className="bg-slate-800 border-slate-700 text-white h-9"
                       data-testid="expense-description-input" />
              </div>
              <div>
                <Label className="text-xs text-slate-400">VAT treatment</Label>
                <VatRateSelect treatment={form.vat_treatment}
                               ratePercent={form.vat_rate_percent}
                               onChange={(p) => setForm({ ...form, ...p })}
                               prefix="expense" />
              </div>
            </div>
            <Button size="sm" disabled={busy} onClick={addExpense}
                    className="bg-saturn-orange hover:bg-saturn-orange/80 text-black"
                    data-testid="expense-save-btn">
              <Plus className="w-3 h-3 mr-1" /> Add expense
            </Button>

            <p className="text-xs text-slate-500" data-testid="finance-ledger-scope">
              Showing {expenses.length} entr{expenses.length === 1 ? "y" : "ies"} for {pnl?.period?.range_label}
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="expense-table">
                <thead className="text-slate-500">
                  <tr>
                    <th className="text-left py-2">Date</th>
                    <th className="text-left">Category</th>
                    <th className="text-left">Payee</th>
                    <th className="text-right">Net</th>
                    <th className="text-right">VAT</th>
                    <th className="text-right">Gross</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {expenses.map((e) => (
                    <React.Fragment key={e.expense_id}>
                    <tr
                        className={`border-t border-slate-800 ${e.status === "voided" ? "opacity-40 line-through" : ""}`}
                        data-testid={`expense-row-${e.expense_id}`}>
                      <td className="py-2 text-slate-300">{e.expense_date}</td>
                      <td className="text-slate-300">{e.category_name}</td>
                      <td className="text-slate-400">{e.payee || "—"}</td>
                      <td className="text-right font-mono text-slate-300">{money(e.net_amount)}</td>
                      <td className="text-right font-mono text-slate-400">{money(e.vat_amount)}</td>
                      <td className="text-right font-mono text-white">
                        {e.amount_gross == null
                          ? <span className="text-amber-300" data-testid={`expense-needs-amount-${e.expense_id}`}>needs amount</span>
                          : money(e.amount_gross)}
                      </td>
                      <td className="text-right whitespace-nowrap">
                        {e.status !== "voided" && (
                          <button onClick={() => startEdit(e)}
                                  className="text-slate-400 hover:text-white mr-2"
                                  title="Edit expense"
                                  data-testid={`expense-edit-btn-${e.expense_id}`}>
                            <Pencil className="w-3 h-3" />
                          </button>
                        )}
                        <label className="inline-flex items-center cursor-pointer text-slate-500 hover:text-white mr-2">
                          <Upload className="w-3 h-3" />
                          <input type="file" className="hidden"
                                 onChange={(ev) => uploadReceipt(e.expense_id, ev.target.files?.[0])}
                                 data-testid={`expense-receipt-input-${e.expense_id}`} />
                        </label>
                        {e.status !== "voided" && (
                          <button onClick={() => voidExpense(e.expense_id)}
                                  className="text-red-400 hover:text-red-300"
                                  title="Void expense"
                                  data-testid={`expense-void-btn-${e.expense_id}`}>
                            <Ban className="w-3 h-3" />
                          </button>
                        )}
                      </td>
                    </tr>
                    {editing === e.expense_id && (
                      <tr className="border-t border-slate-800 bg-slate-800/40"
                          data-testid={`expense-edit-row-${e.expense_id}`}>
                        <td colSpan={7} className="p-3">
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                            <Input type="date" value={editForm.expense_date || ""}
                                   min={bounds?.min_date} max={bounds?.max_date}
                                   onChange={(ev) => setEditForm({ ...editForm, expense_date: ev.target.value })}
                                   className="bg-slate-900 border-slate-700 text-white h-9"
                                   data-testid="expense-edit-date" />
                            <Select value={editForm.category_id}
                                    onValueChange={(v) => setEditForm({ ...editForm, category_id: v })}>
                              <SelectTrigger className="bg-slate-900 border-slate-700 text-white h-9"
                                             data-testid="expense-edit-category">
                                <SelectValue placeholder="Category" />
                              </SelectTrigger>
                              <SelectContent>
                                {activeCategories.map((c) => (
                                  <SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Input type="number" step="0.01" placeholder="Amount (incl. VAT)"
                                   value={editForm.amount_gross}
                                   onChange={(ev) => setEditForm({ ...editForm, amount_gross: ev.target.value })}
                                   className="bg-slate-900 border-slate-700 text-white h-9"
                                   data-testid="expense-edit-amount" />
                            <Input placeholder="Payee" value={editForm.payee}
                                   onChange={(ev) => setEditForm({ ...editForm, payee: ev.target.value })}
                                   className="bg-slate-900 border-slate-700 text-white h-9"
                                   data-testid="expense-edit-payee" />
                            <Input placeholder="Description" value={editForm.description}
                                   onChange={(ev) => setEditForm({ ...editForm, description: ev.target.value })}
                                   className="bg-slate-900 border-slate-700 text-white h-9"
                                   data-testid="expense-edit-description" />
                            <VatRateSelect treatment={editForm.vat_treatment}
                                           ratePercent={editForm.vat_rate_percent}
                                           onChange={(p) => setEditForm({ ...editForm, ...p })}
                                           prefix="expense-edit" dark />
                          </div>
                          <VatPreview gross={editForm.amount_gross}
                                      treatment={editForm.vat_treatment}
                                      ratePercent={editForm.vat_rate_percent}
                                      prefix="expense-edit" />
                          <div className="flex gap-2 mt-2">
                            <Button size="sm" disabled={busy} onClick={saveEdit}
                                    className="bg-saturn-orange hover:bg-saturn-orange/80 text-black"
                                    data-testid="expense-edit-save-btn">
                              <Check className="w-3 h-3 mr-1" /> Save changes
                            </Button>
                            <Button size="sm" variant="outline" className="border-slate-600 text-slate-200"
                                    onClick={() => setEditing(null)}
                                    data-testid="expense-edit-cancel-btn">
                              <X className="w-3 h-3 mr-1" /> Cancel
                            </Button>
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!isLoading && tab === "recurring" && (
          <RecurringTab categories={activeCategories} bounds={bounds} onChanged={load} />
        )}

        {!isLoading && tab === "categories" && (
          <div className="space-y-4" data-testid="finance-categories">
            <div className="rounded border border-slate-800 bg-slate-800/30 p-3 space-y-2">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div>
                  <Label className="text-xs text-slate-400">Name</Label>
                  <Input placeholder="New category name" value={newCategory.name}
                         onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                         className="bg-slate-800 border-slate-700 text-white h-9"
                         data-testid="category-name-input" />
                </div>
                <div>
                  <Label className="text-xs text-slate-400">Group</Label>
                  <Select value={newCategory.group}
                          onValueChange={(v) => setNewCategory({ ...newCategory, group: v })}>
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white h-9"
                                   data-testid="category-group-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {["marketing", "premises", "insurance", "admin", "other"].map((g) => (
                        <SelectItem key={g} value={g}>{g}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-400">Default VAT</Label>
                  <VatRateSelect treatment={newCategory.default_vat_treatment}
                                 ratePercent={newCategory.default_vat_rate_percent}
                                 onChange={(p) => setNewCategory({
                                   ...newCategory,
                                   default_vat_treatment: p.vat_treatment,
                                   default_vat_rate_percent: p.vat_rate_percent })}
                                 prefix="category" />
                </div>
              </div>
              <Button size="sm" onClick={() => createCategory()} className="h-9"
                      data-testid="category-add-btn">
                <Tags className="w-3 h-3 mr-1" /> Add category
              </Button>
            </div>

            {[["Active", true], ["Inactive", false]].map(([heading, isActive]) => {
              const rows = categories.filter((c) => !!c.active === isActive);
              if (!rows.length) return null;
              return (
                <div key={heading} data-testid={`category-section-${heading.toLowerCase()}`}>
                  <p className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">
                    {heading} ({rows.length})
                  </p>
                  {rows.map((c) => (
                    <div key={c.category_id} className="border-b border-slate-800 py-2"
                         data-testid={`category-row-${c.category_id}`}>
                      <div className="flex flex-wrap justify-between items-center gap-2 text-xs">
                        <span className={c.active ? "text-slate-300" : "text-slate-500"}>
                          {c.display_name || c.name}
                          <span className="text-slate-500">
                            {" "}· {c.group} · {c.default_vat_treatment}
                            {c.default_vat_rate_percent != null
                              && ` (${Number(c.default_vat_rate_percent)}%)`}
                          </span>
                        </span>
                        <span className="flex items-center gap-3">
                          <button onClick={() => {
                            setEditingCategory(c.category_id);
                            setCatForm({
                              name: c.name, group: c.group,
                              default_vat_treatment: c.default_vat_treatment,
                              default_vat_rate_percent: c.default_vat_rate_percent
                                ?? presetRate(c.default_vat_treatment) ?? 0,
                            });
                          }} className="text-slate-400 hover:text-white"
                                  data-testid={`category-edit-btn-${c.category_id}`}>
                            <Pencil className="w-3 h-3" />
                          </button>
                          <button onClick={() => toggleCategory(c)}
                                  className={c.active ? "text-slate-400 hover:text-white"
                                                      : "text-emerald-400 hover:text-emerald-300"}
                                  data-testid={c.active
                                    ? `category-toggle-${c.category_id}`
                                    : `category-reactivate-${c.category_id}`}>
                            {c.active ? "Deactivate" : "Reactivate"}
                          </button>
                        </span>
                      </div>
                      {editingCategory === c.category_id && (
                        <div className="mt-2 grid grid-cols-1 sm:grid-cols-4 gap-2 items-start"
                             data-testid={`category-edit-row-${c.category_id}`}>
                          <Input value={catForm.name}
                                 onChange={(e) => setCatForm({ ...catForm, name: e.target.value })}
                                 className="bg-slate-900 border-slate-700 text-white h-9"
                                 data-testid="category-edit-name" />
                          <Select value={catForm.group}
                                  onValueChange={(v) => setCatForm({ ...catForm, group: v })}>
                            <SelectTrigger className="bg-slate-900 border-slate-700 text-white h-9"
                                           data-testid="category-edit-group">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {["marketing", "premises", "insurance", "admin", "other"].map((g) => (
                                <SelectItem key={g} value={g}>{g}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <VatRateSelect treatment={catForm.default_vat_treatment}
                                         ratePercent={catForm.default_vat_rate_percent}
                                         onChange={(p) => setCatForm({
                                           ...catForm,
                                           default_vat_treatment: p.vat_treatment,
                                           default_vat_rate_percent: p.vat_rate_percent })}
                                         prefix="category-edit" dark />
                          <div className="flex gap-2">
                            <Button size="sm" onClick={saveCategoryEdit}
                                    className="bg-saturn-orange hover:bg-saturn-orange/80 text-black"
                                    data-testid="category-edit-save-btn">
                              <Check className="w-3 h-3 mr-1" /> Save
                            </Button>
                            <Button size="sm" variant="outline"
                                    className="border-slate-600 text-slate-200"
                                    onClick={() => setEditingCategory(null)}
                                    data-testid="category-edit-cancel-btn">
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              );
            })}
            <p className="text-[11px] text-slate-500">
              Changing a default VAT rate only affects NEW entries — anything already
              posted keeps the rate it was saved with.
            </p>
          </div>
        )}
      </CardContent>

      <Dialog open={!!archivedPrompt} onOpenChange={(o) => !o && setArchivedPrompt(null)}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">That name is already archived</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-400" data-testid="archived-prompt-message">
            An inactive &apos;{archivedPrompt?.name}&apos; exists. Reactivate it (keeps all
            its history) or create a new one.
          </p>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button size="sm" className="bg-saturn-orange hover:bg-saturn-orange/80 text-black"
                    onClick={async () => {
                      const ok = await patchCategory(archivedPrompt.category_id,
                                                     { active: true });
                      if (ok) {
                        setArchivedPrompt(null);
                        setNewCategory({ name: "", group: "other",
                                         default_vat_treatment: "standard_23",
                                         default_vat_rate_percent: 23 });
                      }
                    }}
                    data-testid="archived-prompt-reactivate">
              Reactivate it
            </Button>
            <Button size="sm" variant="outline" className="border-slate-600 text-slate-200"
                    onClick={() => createCategory(true)}
                    data-testid="archived-prompt-create-new">
              Create a new one
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!templateWarning} onOpenChange={(o) => !o && setTemplateWarning(null)}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Recurring overheads still use this</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-400" data-testid="template-warning-message">
            {templateWarning?.message}
          </p>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button size="sm" variant="outline" className="border-slate-600 text-slate-200"
                    onClick={() => patchCategory(templateWarning.categoryId,
                                                 templateWarning.body,
                                                 "?deactivate_templates=false")}
                    data-testid="template-warning-keep">
              Keep them running
            </Button>
            <Button size="sm" className="bg-saturn-orange hover:bg-saturn-orange/80 text-black"
                    onClick={() => patchCategory(templateWarning.categoryId,
                                                 templateWarning.body,
                                                 "?deactivate_templates=true")}
                    data-testid="template-warning-deactivate">
              Stop those templates too
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <InternalStockUseDialog
        open={showInternalUse}
        onClose={() => setShowInternalUse(false)}
        periodParams={Object.fromEntries(new URLSearchParams(query))}
      />

      <ExternalCarWashDialog
        open={showCarWash}
        onClose={() => setShowCarWash(false)}
        periodParams={Object.fromEntries(new URLSearchParams(query))}
      />

      <RedoWarrantyCostDialog
        open={showRedoCost}
        onClose={() => setShowRedoCost(false)}
        periodParams={Object.fromEntries(new URLSearchParams(query))}
      />
      </Card>

      {/* Stock on hand — a BALANCE SHEET figure, deliberately OUTSIDE the
          P&L card, and included in no profit number. */}
      <StockValuationCard />
    </>
  );
};

export default FinanceSection;
